import type { GameAction } from "@golazo/shared";
import { t } from "../i18n/index.js";

/**
 * Los controles del teclado, a gusto de cada uno.
 *
 * Pedido en Discord (2026-09-29): "¿se pueden cambiar los controles?". Cada
 * accion tiene dos teclas (principal y alternativa: por defecto WASD y las
 * flechas para moverse) y se guardan en este navegador. Es input, no fisica:
 * cambian que tecla produce el `InputFrame`, no lo que el partido hace con el,
 * asi que en online cada uno puede tener las suyas sin desincronizar nada.
 *
 * Las teclas son `KeyboardEvent.code` (la tecla FISICA): la W de un QWERTY y la
 * Z de un AZERTY son la misma, y el nombre que se enseña sale de la
 * distribucion del teclado cuando el navegador la da (`keyLabel`).
 */
export type BindableAction = "moveUp" | "moveDown" | "moveLeft" | "moveRight" | "kick" | "dash";

export const BINDABLE_ACTIONS: readonly BindableAction[] = [
  "moveUp",
  "moveDown",
  "moveLeft",
  "moveRight",
  "kick",
  "dash",
];

/** Principal y alternativa. `null` = sin tecla. */
export type KeySlots = readonly [string | null, string | null];

export interface ControlSettings {
  keys: Readonly<Record<BindableAction, KeySlots>>;
  /**
   * Golpear con el clic izquierdo, hacia el raton (ver `input/mouseAim.ts`).
   * Con el raton se apunta al grado; con el teclado, en 8 direcciones.
   */
  mouseAim: boolean;
}

export const DEFAULT_CONTROLS: ControlSettings = {
  keys: {
    moveUp: ["KeyW", "ArrowUp"],
    moveDown: ["KeyS", "ArrowDown"],
    moveLeft: ["KeyA", "ArrowLeft"],
    moveRight: ["KeyD", "ArrowRight"],
    kick: ["KeyK", null],
    dash: ["KeyO", null],
  },
  mouseAim: true,
};

/**
 * Teclas que no se pueden asignar: ya son de algo. Esc sale del partido, Tab
 * enseña las estadisticas y Enter abre el chat; las del sistema (Windows,
 * menu contextual, F5 recargar…) el navegador no siempre las deja llegar.
 */
const RESERVED = new Set([
  "Escape",
  "Tab",
  "Enter",
  "NumpadEnter",
  "MetaLeft",
  "MetaRight",
  "OSLeft",
  "OSRight",
  "ContextMenu",
  "F5",
  "F11",
  "F12",
]);

export function isReservedKey(code: string): boolean {
  return RESERVED.has(code);
}

const STORAGE_KEY = "fynta.controls";

function validSlot(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 && v.length <= 32 && !RESERVED.has(v) ? v : null;
}

/** Lo guardado, o lo de fabrica. Nunca lanza: sin almacenamiento, los de fabrica. */
export function loadControls(): ControlSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_CONTROLS;
    const data = JSON.parse(raw) as { keys?: Record<string, unknown>; mouseAim?: unknown };
    const keys = {} as Record<BindableAction, KeySlots>;
    for (const action of BINDABLE_ACTIONS) {
      const slots = data.keys?.[action];
      keys[action] = Array.isArray(slots)
        ? [validSlot(slots[0]), validSlot(slots[1])]
        : DEFAULT_CONTROLS.keys[action];
    }
    return { keys, mouseAim: typeof data.mouseAim === "boolean" ? data.mouseAim : DEFAULT_CONTROLS.mouseAim };
  } catch {
    return DEFAULT_CONTROLS;
  }
}

const listeners = new Set<() => void>();

export function onControlsChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function saveControls(settings: ControlSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Sin almacenamiento duran lo que dure la pestaña: mejor eso que nada.
  }
  memory = settings;
  for (const fn of listeners) fn();
}

/** Lo ultimo guardado en esta pestaña (el almacenamiento puede no existir). */
let memory: ControlSettings | null = null;

export function currentControls(): ControlSettings {
  return memory ?? loadControls();
}

/**
 * Pone `code` en esa accion y hueco, y lo QUITA de donde estuviera: una tecla
 * que hace dos cosas es un partido perdido.
 */
export function assignKey(
  settings: ControlSettings,
  action: BindableAction,
  slot: 0 | 1,
  code: string | null,
): ControlSettings {
  const keys = {} as Record<BindableAction, KeySlots>;
  for (const a of BINDABLE_ACTIONS) {
    const [p, s] = settings.keys[a];
    keys[a] = [p === code ? null : p, s === code ? null : s];
  }
  const current = [...keys[action]] as [string | null, string | null];
  current[slot] = code;
  keys[action] = current;
  return { ...settings, keys };
}

/** El mapa que usa el teclado del partido: tecla -> accion. */
export function keyMap(settings: ControlSettings = currentControls()): Record<string, GameAction> {
  const map: Record<string, GameAction> = {};
  for (const action of BINDABLE_ACTIONS) {
    for (const code of settings.keys[action]) if (code) map[code] = action;
  }
  return map;
}

/** Acciones que se quedan sin ninguna tecla (se avisa en la pantalla de controles). */
export function unboundActions(settings: ControlSettings): BindableAction[] {
  return BINDABLE_ACTIONS.filter((a) => !settings.keys[a][0] && !settings.keys[a][1]);
}

// --- nombres de tecla ---

/**
 * Lo que dice cada tecla en ESTE teclado, si el navegador lo sabe
 * (`navigator.keyboard.getLayoutMap`, Chrome y Edge). En un AZERTY, `KeyW` es
 * la Z; sin esto se enseñaria "W" y no habria forma de encontrarla.
 */
let layout: Map<string, string> | null = null;

export async function primeKeyLabels(): Promise<void> {
  const kb = (navigator as Navigator & {
    keyboard?: { getLayoutMap?: () => Promise<Map<string, string>> };
  }).keyboard;
  if (!kb?.getLayoutMap || layout) return;
  try {
    layout = await kb.getLayoutMap();
    for (const fn of listeners) fn();
  } catch {
    // Sin permiso o sin API: se queda el nombre por codigo.
  }
}

const NAMED: Record<string, string> = {
  ArrowUp: "↑",
  ArrowDown: "↓",
  ArrowLeft: "←",
  ArrowRight: "→",
  ShiftLeft: "⇧",
  ShiftRight: "⇧",
  ControlLeft: "Ctrl",
  ControlRight: "Ctrl",
  AltLeft: "Alt",
  AltRight: "AltGr",
  Backspace: "⌫",
  CapsLock: "Bloq",
  Backquote: "º",
};

/** El nombre corto de una tecla, para el cartel (`<kbd>`). */
export function keyLabel(code: string | null): string {
  if (!code) return "—";
  const fromLayout = layout?.get(code);
  if (fromLayout && fromLayout.trim()) return fromLayout.toUpperCase();
  if (code === "Space") return t("Espacio");
  if (NAMED[code]) return NAMED[code]!;
  if (code.startsWith("Key")) return code.slice(3);
  if (code.startsWith("Digit")) return code.slice(5);
  if (code.startsWith("Numpad")) return `Num ${code.slice(6)}`;
  return code;
}

/** La tecla principal de una accion (o la alternativa si no hay principal). */
export function primaryKey(action: BindableAction, settings: ControlSettings = currentControls()): string {
  const [p, s] = settings.keys[action];
  return keyLabel(p ?? s);
}

/** Las cuatro de moverse, en el orden de WASD (arriba, izquierda, abajo, derecha). */
export function moveKeys(settings: ControlSettings = currentControls()): string[] {
  return (["moveUp", "moveLeft", "moveDown", "moveRight"] as const).map((a) => primaryKey(a, settings));
}
